'use client'

import { useEffect, useMemo, useState } from 'react'
import { IconSquareKey } from '@tabler/icons-react'
import { object, string } from 'yup'

import Input from 'shared/ui/form/Input'
import PasswordField from 'shared/ui/form/PasswordField'
import { validateField, validateValues } from 'shared/utils/form'
import { passwordRegex } from 'shared/utils/validators'

import { useCustomerData } from '../hooks/useCustomerData'

import ProfileDetailsSingleItem from './ProfileDetailsSingleItem'

interface FormPayload {
  currentPassword: string
  newPassword: string
  newPasswordConfirmation: string
}

const schema = object().shape({
  currentPassword: string().required('Hasło jest wymagane'),
  newPassword: string()
    .required('Nowe hasło jest wymagane')
    .matches(passwordRegex, 'Niepoprawny format hasła'),
  newPasswordConfirmation: string().test(
    'password-match',
    'Podane hasła muszą być identyczne',
    function (value) {
      const parent = this.parent as { newPassword: string }
      const password = parent.newPassword

      return password === value
    }
  )
})

const getFieldError = (values: FormPayload, name: keyof FormPayload) => {
  try {
    schema.validateSyncAt(name, values)

    return undefined
  } catch (e) {
    return (e as Error).message
  }
}

const validateFormField = (values: FormPayload) => validateField(schema, values)
const validateFormValues = validateValues(schema)

const initialValues = {
  currentPassword: '',
  newPassword: '',
  newPasswordConfirmation: ''
}

const CustomerPasswordUpdateForm = () => {
  const [values, setValues] = useState(initialValues)
  const [valid, setValid] = useState(false)
  const [newPasswordTouched, setNewPasswordTouched] = useState(false)
  const { user } = useCustomerData()

  // Memoize the validation function to prevent infinite re-renders
  const memoizedValidateFormField = useMemo(() => {
    return validateFormField(values)
  }, [values])

  const newPasswordError = newPasswordTouched
    ? getFieldError(values, 'newPassword')
    : undefined

  const handleValueChange = (name: string, value: string) => {
    setValues(prev => ({ ...prev, [name]: value }))
  }

  useEffect(() => {
    values && validateFormValues(values, setValid)
  }, [values])

  return (
    <ProfileDetailsSingleItem
      id='profile_edit_password'
      modalTitle='Zmień hasło'
      isSubmitDisabled={!valid}
      icon={<IconSquareKey />}
      label='Hasło'
      value='*******'
      values={values}
      buttonText='Zmień hasło'
      openCallback={() => {}}
    >
      <input
        type='email'
        name='username'
        autoComplete='username'
        value={user?.email ?? ''}
        readOnly
        hidden
      />
      <Input
        id='password_update_current_password'
        required
        inputProps={{ autoComplete: 'current-password' }}
        label='Obecne hasło'
        type='password'
        name='currentPassword'
        onChange={handleValueChange}
        value={values.currentPassword}
        validate={memoizedValidateFormField}
      />
      <PasswordField
        id='password_update_new_password'
        required
        withPasswordHint
        valueToPasswordHint={values.newPassword}
        inputProps={{ autoComplete: 'new-password' }}
        label='Nowe hasło'
        name='newPassword'
        value={values.newPassword}
        onChange={e => handleValueChange(e.target.name, e.target.value)}
        onBlur={() => setNewPasswordTouched(true)}
        error={Boolean(newPasswordError)}
        helperText={newPasswordError}
      />
      <Input
        id='password_update_new_password_confirmation'
        required
        inputProps={{ autoComplete: 'new-password' }}
        label='Potwierdź nowe hasło'
        type='password'
        name='newPasswordConfirmation'
        onChange={handleValueChange}
        value={values.newPasswordConfirmation}
        validate={memoizedValidateFormField}
      />
    </ProfileDetailsSingleItem>
  )
}

export default CustomerPasswordUpdateForm
